import { useState, useRef, useEffect } from "react"
import { Platform, Pressable, View } from "react-native"
import { useRouter } from "expo-router"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@src/components/ui/sheet"
import { ar } from "@src/i18n"
import { Text } from "./core"

interface ProductCardActionsProps {
  productId: number
  productName: string
}

export function ProductCardActions({ productId, productName }: ProductCardActionsProps) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<View>(null)

  useEffect(() => {
    if (Platform.OS === "web" && open) {
      const handleClickOutside = (event: MouseEvent) => {
        // On Web, the ref.current is an HTMLElement, but typed as View
        const domNode = wrapperRef.current as unknown as HTMLElement
        if (domNode && !domNode.contains(event.target as Node)) {
          setOpen(false)
        }
      }
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  const handleEdit = () => {
    setOpen(false)
    router.push(`/dashboard/products/${productId}/edit`)
  }

  if (Platform.OS === "web") {
    return (
      <View className="absolute top-2 end-2 z-50" ref={wrapperRef}>
        <Pressable
          onPress={() => setOpen(!open)}
          className="p-1.5 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full shadow-sm hover:shadow-md transition-all border border-gray-100/50"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <MIcon name="dots-vertical" size={18} color="#4B5563" />
        </Pressable>

        {open && (
          <Pressable
            onPress={handleEdit}
            className="absolute end-0 top-full mt-1 h-10 w-32 bg-white rounded shadow-sm border border-gray-200 z-50 flex-row items-center px-3 hover:bg-gray-50 active:bg-blue-50 transition-colors"
          >
            <MIcon name="pencil" size={20} color="#4B5563" />
            <Text className="ms-2 text-sm text-gray-700 font-medium">{ar.edit}</Text>
          </Pressable>
        )}
      </View>
    )
  }

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        className="absolute top-2 end-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow-sm z-10"
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <MIcon name="dots-vertical" size={18} color="#6B7280" />
      </Pressable>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader className="flex-row items-center justify-between">
            <SheetTitle>{productName}</SheetTitle>
            <SheetClose onPress={() => setOpen(false)} />
          </SheetHeader>

          <View className="py-4 gap-y-2">
            <Pressable onPress={handleEdit} className="flex-row items-center p-4 bg-gray-50 rounded-xl active:bg-gray-100">
              <View className="w-10 h-10 rounded-full bg-blue-100 items-center justify-center me-3">
                <MIcon name="pencil" size={20} color="#2563EB" />
              </View>
              <View>
                <Text className="font-semibold text-gray-900 text-lg">{ar.edit}</Text>
                <Text className="text-gray-500 text-sm">Edit product details, prices and stock</Text>
              </View>
            </Pressable>

            <Pressable onPress={() => setOpen(false)} className="mt-2 p-4 items-center justify-center rounded-xl border border-gray-200">
              <Text className="font-semibold text-gray-900">{ar.cancel}</Text>
            </Pressable>
          </View>
        </SheetContent>
      </Sheet>
    </>
  )
}
